import { useCallback, useEffect, useRef, useState } from 'react';
import { appendUnique } from '../../utils/append-unique';
import { recallBrowseList, rememberBrowseList } from '../../utils/browse-list-cache';
import { isBackNavigation } from '../../utils/navigation-direction';
import { PAGE_SIZE } from '../../data/js/pagination';
import { trackError, ErrorSeverity } from '../../utils/error-tracking';

/** One page from the server: its listings, and whether there is another. */
export interface ListPage<T> {
  items: T[];
  hasMore: boolean;
}

/** Where the list lives when the page does not own it (the seller grid keeps it in the product context). */
export interface ExternalListStore<T> {
  list: readonly T[];
  setList: (list: T[], page: number) => void;
}

export interface PagedListOptions<T extends { id?: unknown }> {
  /** Whose list, in which order. A new key is a new list; also the Back cache key. */
  listKey: string;
  fetchPage: (page: number) => Promise<ListPage<T>>;
  /** False holds every fetch (no reader yet, another view showing). */
  enabled?: boolean;
  /** Keep the list outside the hook. Page 1 then comes from whoever fills it. */
  store?: ExternalListStore<T>;
  /** Called with a failed fetch (default: trackError). The page is given back, so the next ask retries it. */
  onError?: (error: unknown) => void;
  pageSize?: number;
}

export interface PagedList<T> {
  list: readonly T[];
  /** Pages the reader has asked for. useScrollRestoration saves and restores it. */
  page: number;
  setPage: (page: number) => void;
  done: boolean;
  loading: boolean;
  /** Ask for the next page, once: a call while loading, or after the last page, does nothing. */
  loadNext: () => void;
}

/**
 * The one pager behind the plain grids (For You, the seller page): every one
 * counts pages, ends, guards and dedups the same way.
 *
 * `page` is how many pages the reader has asked for; `loadedPage` how many
 * have arrived. The fetch effect takes the next missing page until they meet,
 * in order, so a restored page count (useScrollRestoration on Back) can never
 * leave a gap. On Back the list the reader left comes back from the in-memory
 * list cache and nothing is fetched: the shuffle seed is per page load, so a
 * refetch would bring back different listings.
 */
export function usePagedList<T extends { id?: unknown }>({
  listKey,
  fetchPage,
  enabled = true,
  store,
  onError,
  pageSize = PAGE_SIZE.LISTINGS,
}: PagedListOptions<T>): PagedList<T> {
  const external = !!store;
  const pagesIn = (length: number) => Math.ceil(length / pageSize);

  const [kept] = useState(() => (!external && isBackNavigation() ? recallBrowseList<T>(listKey) : null));
  const [ownList, setOwnList] = useState<T[]>(() => (kept ? [...kept] : []));
  const list = store ? store.list : ownList;

  // An external list arrives with page 1 already in it (or on its way); the
  // hook's own list starts by fetching it.
  const startPages = pagesIn((store ? store.list : kept ?? []).length);
  const [page, setPage] = useState(external ? startPages : Math.max(1, startPages));
  const [loadedPage, setLoadedPage] = useState(startPages);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  // Set before any re-render, so two calls in one tick ask for one page.
  const loadingRef = useRef(false);

  // A new key is a new list: nothing of the old one kept. Adjusted during
  // render (React's "reset state on prop change"), so the fetch below never
  // runs once with the new key and the old page. An outside list still holds
  // the OLD key's listings until its owner replaces them; it is remembered so
  // it is not counted as the new list's pages (a search 5 pages deep would
  // otherwise have its next search start at page 6).
  const [shownKey, setShownKey] = useState(listKey);
  const [staleList, setStaleList] = useState<readonly T[] | null>(null);
  if (shownKey !== listKey) {
    setShownKey(listKey);
    setPage(external ? 0 : 1);
    setLoadedPage(0);
    setDone(false);
    if (external) setStaleList(list);
    else setOwnList([]);
  }

  // A list filled from outside (page 1, a Back restore) counts as loaded.
  const outsidePages = external && list !== staleList ? pagesIn(list.length) : 0;
  if (outsidePages > loadedPage && !done) {
    setLoadedPage(outsidePages);
    if (outsidePages > page) setPage(outsidePages);
  }

  // What Back puts back.
  useEffect(() => {
    rememberBrowseList(listKey, list);
  }, [listKey, list]);

  const latest = useRef({ fetchPage, store, onError, list });
  useEffect(() => {
    latest.current = { fetchPage, store, onError, list };
  });

  useEffect(() => {
    if (!enabled || done || loadedPage >= page) return;
    const nextPage = loadedPage + 1;
    let cancelled = false;

    const run = async () => {
      loadingRef.current = true;
      setLoading(true);
      try {
        const { items, hasMore } = await latest.current.fetchPage(nextPage);
        if (cancelled) return;
        const { store: target, list: shown } = latest.current;
        const next = nextPage === 1 && !target ? items : appendUnique(shown, items);
        // A page that adds nothing leaves the list as it is.
        if (next.length !== shown.length || (nextPage === 1 && !target)) {
          if (target) target.setList(next, nextPage);
          else setOwnList(next);
        }
        setLoadedPage(nextPage);
        // A last page stops the walk where it is, however many were asked for.
        if (!hasMore) setDone(true);
      } catch (error) {
        const report = latest.current.onError;
        if (report) report(error);
        else trackError(error instanceof Error ? error : String(error), ErrorSeverity.WARNING, { component: 'usePagedList', action: 'fetchPage' });
        // Not the end of the list: hand the page back so the button retries it.
        if (!cancelled) setPage(nextPage - 1);
      } finally {
        if (!cancelled) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
      loadingRef.current = false;
    };
  }, [enabled, done, loadedPage, page, listKey]);

  const loadNext = useCallback(() => {
    if (!enabled || loadingRef.current || done) return;
    loadingRef.current = true;
    setLoading(true);
    setPage(Math.max(page, loadedPage) + 1);
  }, [enabled, done, page, loadedPage]);

  return { list, page, setPage, done, loading, loadNext };
}
